import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  getCurrentUser,
  fetchAuthSession,
  signIn,
  signOut,
  confirmSignIn,
  setUpTOTP,
  verifyTOTPSetup,
  resetPassword,
  confirmResetPassword,
  type SignInInput,
} from "aws-amplify/auth";
import type { AuthUser, AuthState, UserRole } from "./types";

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<{ nextStep: string }>;
  confirmNewPassword: (newPassword: string) => Promise<{ nextStep: string }>;
  setupTotp: () => Promise<{ totpUri: string; secretCode: string }>;
  verifyTotpSetup: (code: string) => Promise<void>;
  confirmMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<{ destination?: string }>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (...roles: UserRole[]) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const loadUser = useCallback(async () => {
    try {
      const cognitoUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups =
        (session.tokens?.idToken?.payload?.["cognito:groups"] as string[]) ??
        [];

      const user: AuthUser = {
        email: cognitoUser.signInDetails?.loginId ?? "",
        sub: cognitoUser.userId,
        roles: groups as UserRole[],
      };

      setState({ user, isAuthenticated: true, isLoading: false });
    } catch {
      setState({ user: null, isAuthenticated: false, isLoading: false });
    }
  }, []);

  useEffect(() => {
    loadUser(); // eslint-disable-line react-hooks/set-state-in-effect -- initial auth check on mount is intentional
  }, [loadUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await signIn({
        username: email,
        password,
      } as SignInInput);
      if (result.isSignedIn) {
        await loadUser();
      }
      return { nextStep: result.nextStep.signInStep };
    },
    [loadUser],
  );

  const confirmNewPassword = useCallback(
    async (newPassword: string) => {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        await loadUser();
      }
      return { nextStep: result.nextStep.signInStep };
    },
    [loadUser],
  );

  const setupTotp = useCallback(async () => {
    const totpSetup = await setUpTOTP();
    const uri = totpSetup.getSetupUri("MDKL Link Management");
    return { totpUri: uri.toString(), secretCode: totpSetup.sharedSecret };
  }, []);

  const verifyTotpSetup = useCallback(
    async (code: string) => {
      await verifyTOTPSetup({ code });
      // After TOTP is verified, we need to confirm the sign-in challenge
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.isSignedIn) {
        await loadUser();
      }
    },
    [loadUser],
  );

  const confirmMfa = useCallback(
    async (code: string) => {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.isSignedIn) {
        await loadUser();
      }
    },
    [loadUser],
  );

  const forgotPassword = useCallback(async (email: string) => {
    const result = await resetPassword({ username: email });
    const destination = result.nextStep.codeDeliveryDetails?.destination;
    return { destination };
  }, []);

  const confirmForgotPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
    },
    [],
  );

  const logout = useCallback(async () => {
    await signOut();
    setState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  const hasRole = useCallback(
    (role: UserRole) => state.user?.roles.includes(role) ?? false,
    [state.user],
  );

  const hasAnyRole = useCallback(
    (...roles: UserRole[]) => roles.some((r) => state.user?.roles.includes(r)),
    [state.user],
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        confirmNewPassword,
        setupTotp,
        verifyTotpSetup,
        confirmMfa,
        logout,
        forgotPassword,
        confirmForgotPassword,
        hasRole,
        hasAnyRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
